/* eslint-disable camelcase */
import React, { useState } from "react"
import Header from "../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import {
  Box,
  Button,
  Stack,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  Chip,
  LinearProgress,
} from "@mui/material"
import fetchwrapper from "../../services/interceptors/fetchwrapper"
import BackIcon from "../../components/BackIcon"
import CustomBackdrop from "../../components/CustomBackdrop"
import CustomFieldsetAccordion from "../../components/CustomFieldsetAccordion"
import CustomAggregationSegregationComponent from "../../components/CustomAggregationSegregationComponent"
import CustomDatePicker from "../../components/CustomDatePicker"
import { useQuery } from "@tanstack/react-query"
import dayjs from "dayjs"
import { showWarning } from "../../api"

// Íconos Ejecutivos
import RefreshIcon from "@mui/icons-material/Refresh"
import SearchIcon from "@mui/icons-material/Search"
import Inventory2Icon from "@mui/icons-material/Inventory2"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import StyleIcon from "@mui/icons-material/Style"
import MapIcon from "@mui/icons-material/Map"
import DownloadIcon from "@mui/icons-material/Download" // Icono para exportar

const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#2e7d32" },
    error: { main: "#d32f2f" },
    warning: { main: "#ed6c02" },
    info: { main: "#0288d1" },
  },
})

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(4),
  flexGrow: 1,
  padding: "0 16px",
  minHeight: "100vh",
}))

// Estilo para Tarjetas Ejecutivas (C-Level)
const KPICard = styled(Card)(({ theme, bgcolor }) => ({
  borderRadius: "16px",
  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
  background: bgcolor || "#ffffff",
  color: bgcolor ? "#ffffff" : "#333",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  overflow: "hidden",
  transition: "transform 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
  },
}))

// --- HOOKS DE REACT QUERY ---

function useGetCatalogosBodegas() {
  return useQuery({
    queryKey: ["CatalogosResumenBodegas"],
    queryFn: async () => {
      const response = await fetchwrapper(`/ResumenProductosbodegas/getCatalogosResumenBodegas`)
      const result = await response.json()
      return result.data?.bodegas || []
    },
    refetchOnWindowFocus: false,
  })
}

function useGetKpisBodega() {
  return useQuery({
    queryKey: ["KpisResumenBodegas"],
    queryFn: async () => {
      const options = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ externalFilters: {} }), // Se evalúan todas las bodegas permitidas
      }
      const response = await fetchwrapper(`/ResumenProductosbodegas/getKpisBodega`, options)
      const result = await response.json()
      return result.data || {}
    },
    refetchOnWindowFocus: false,
  })
}

function useGetTablaInventario(filtros) {
  return useQuery({
    queryKey: ["TablaResumenBodegas", filtros],
    queryFn: async () => {
      const payload = {
        externalFilters: {
          fecVenDesde: filtros.fecVenDesde ? dayjs(filtros.fecVenDesde).format("YYYY-MM-DD") : "",
          fecVenHasta: filtros.fecVenHasta ? dayjs(filtros.fecVenHasta).format("YYYY-MM-DD") : "",
          soloVencidos: filtros.soloVencidos,
          soloPorVencer: filtros.soloPorVencer,
        },
      }
      const options = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
      const response = await fetchwrapper(`/ResumenProductosbodegas/getAllInfo`, options)
      const result = await response.json()
      return result.data || []
    },
    enabled: false,
  })
}

const ResumenProductosBodegas = () => {
  const [expanded, setExpanded] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  // Filtros limpios enfocados en BI
  const [filtros, setFiltros] = useState({
    fecVenDesde: null,
    fecVenHasta: null,
    soloVencidos: false,
    soloPorVencer: false,
  })

  // Queries
  const { data: listaBodegas = [], isLoading: loadBodegas } = useGetCatalogosBodegas()
  const { data: kpiData = {}, isLoading: loadKpi, isFetching: fetchKpi, refetch: refetchKpi } = useGetKpisBodega()
  const {
    data: tablaData = [],
    isLoading: loadTabla,
    isFetching: fetchTabla,
    refetch: refetchTabla,
  } = useGetTablaInventario(filtros)

  // Título dinámico con los nombres de las bodegas
  const nombresBodegasStr = listaBodegas.map((b) => b.boddescri).join(" / ") || "CARGANDO..."

  const handleToggleFiltros = () => setExpanded(!expanded)

  const handleEjecutarBusqueda = async () => {
    setHasSearched(true)
    setExpanded(false)
    await refetchTabla()
  }

  // Función para exportar la data JSON a CSV (Compatible con Excel UTF-8)
  const handleExportarCSV = () => {
    if (!tablaData || tablaData.length === 0) {
      return showWarning("No hay datos disponibles para exportar.")
    }

    const headers = [
      "Bodega",
      "Familia",
      "Marca",
      "Código",
      "Descripción",
      "Zona",
      "Pasillo",
      "Posición",
      "Stock Físico",
      "Stock Útil",
      "Lotes Vencidos",
      "Por Vencer (Riesgo)",
      "Fec. Próx. Caducidad",
    ]

    const csvContent = [
      headers.join(","),
      ...tablaData.map((row) => {
        return [
          `"${row.boddescri || ""}"`,
          `"${row.familia || ""}"`,
          `"${row.marca || ""}"`,
          `"${row.artcodigo || ""}"`,
          `"${row.artdescri ? row.artdescri.replace(/"/g, '""') : ""}"`, // Escapar comillas dobles
          `"${row.zona || ""}"`,
          `"${row.pasillo || ""}"`,
          `"${row.posicion || ""}"`,
          row.stock_bruto || 0,
          row.stock_util || 0,
          row.vencidos || 0,
          row.por_vencer || 0,
          row.fecha_vencimiento ? dayjs(row.fecha_vencimiento).format("DD/MM/YYYY") : "N/A",
        ].join(",")
      }),
    ].join("\n")

    // \uFEFF es el BOM (Byte Order Mark) que fuerza a Excel a leerlo como UTF-8
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.setAttribute("download", `Resumen_Inventario_${dayjs().format("YYYYMMDD_HHmm")}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const isLoadingGlobal = loadBodegas || loadKpi || fetchKpi || loadTabla || fetchTabla

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <CustomBackdrop isLoading={isLoadingGlobal} />

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 20px 30px",
            fontSize: "25px",
            textAlign: "center",
          }}
        >
          <b>Inventario: {nombresBodegasStr}</b>
        </div>

        <Box className={StyledRoot}>
          {/* =================================================================== */}
          {/* SECCIÓN A: KPIS EJECUTIVOS                                          */}
          {/* =================================================================== */}
          <Box mb={4}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5" color="primary" fontWeight="bold" sx={{ display: "flex", alignItems: "center" }}>
                <Inventory2Icon sx={{ mr: 1, fontSize: 32 }} />
                Dashboard Ejecutivo
              </Typography>
              <Tooltip title="Actualizar Indicadores">
                <IconButton onClick={() => refetchKpi()} sx={{ bgcolor: "white", boxShadow: 1 }}>
                  <RefreshIcon color="primary" />
                </IconButton>
              </Tooltip>
            </Stack>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard bgcolor="linear-gradient(135deg, #196C87 0%, #114b5e 100%)">
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ opacity: 0.8, textTransform: "uppercase" }}>
                      Stock Físico (Bruto)
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" mt={1}>
                      {Number(kpiData.total_stock_bruto || 0).toLocaleString()}
                    </Typography>
                    <Inventory2Icon
                      sx={{ position: "absolute", right: -10, bottom: -10, fontSize: 100, opacity: 0.15 }}
                    />
                  </CardContent>
                </KPICard>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <KPICard bgcolor="linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)">
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ opacity: 0.8, textTransform: "uppercase" }}>
                      Stock Comercial (Útil)
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" mt={1}>
                      {Number(kpiData.total_stock_util || 0).toLocaleString()}
                    </Typography>
                    <CheckCircleOutlineIcon
                      sx={{ position: "absolute", right: -10, bottom: -10, fontSize: 100, opacity: 0.15 }}
                    />
                  </CardContent>
                </KPICard>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <KPICard bgcolor="linear-gradient(135deg, #d32f2f 0%, #9a0007 100%)">
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ opacity: 0.8, textTransform: "uppercase" }}>
                      Merma: Lotes Vencidos
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" mt={1}>
                      {Number(kpiData.total_lotes_vencidos || 0).toLocaleString()}
                    </Typography>
                    <ErrorOutlineIcon
                      sx={{ position: "absolute", right: -10, bottom: -10, fontSize: 100, opacity: 0.15 }}
                    />
                  </CardContent>
                </KPICard>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <KPICard bgcolor="linear-gradient(135deg, #ed6c02 0%, #e65100 100%)">
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ opacity: 0.8, textTransform: "uppercase" }}>
                      Riesgo: Por Vencer (30d)
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" mt={1}>
                      {Number(kpiData.total_lotes_riesgo || 0).toLocaleString()}
                    </Typography>
                    <WarningAmberIcon
                      sx={{ position: "absolute", right: -10, bottom: -10, fontSize: 100, opacity: 0.15 }}
                    />
                  </CardContent>
                </KPICard>
              </Grid>

              {/* Fila Secundaria de KPIs: SKUs y Zonas */}
              <Grid item xs={12} md={4}>
                <KPICard>
                  <CardContent
                    sx={{ display: "flex", flexDirection: "column", height: "100%", justifyContent: "center" }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box sx={{ p: 2, bgcolor: "#f3e5f5", borderRadius: "12px" }}>
                        <StyleIcon sx={{ color: "#9c27b0", fontSize: 40 }} />
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary" textTransform="uppercase">
                          Portafolio Almacenado
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" color="#9c27b0">
                          {Number(kpiData.total_codigos_productos || 0).toLocaleString()} SKUs
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </KPICard>
              </Grid>

              <Grid item xs={12} md={8}>
                <KPICard>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                      <MapIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        Top Zonas de Almacenamiento (Posiciones Ocupadas)
                      </Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      {(kpiData.posiciones_por_zona || []).slice(0, 4).map((z, idx) => (
                        <Grid item xs={12} sm={6} key={idx}>
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2" fontWeight="bold">
                              {z.zona}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {z.posiciones_ocupadas} pos.
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, (z.posiciones_ocupadas / 500) * 100)}
                            sx={{ height: 8, borderRadius: 4, bgcolor: "#e0e0e0" }}
                            color={idx % 2 === 0 ? "primary" : "info"}
                          />
                        </Grid>
                      ))}
                      {(!kpiData.posiciones_por_zona || kpiData.posiciones_por_zona.length === 0) && (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ width: "100%", textAlign: "center", py: 2 }}
                        >
                          No hay información de zonas disponible.
                        </Typography>
                      )}
                    </Grid>
                  </CardContent>
                </KPICard>
              </Grid>
            </Grid>
          </Box>

          {/* =================================================================== */}
          {/* SECCIÓN B: FILTROS DE VENCIMIENTOS                                  */}
          {/* =================================================================== */}
          <CustomFieldsetAccordion
            title="Auditoría de Vencimientos y Riesgos"
            expanded={expanded}
            onToggle={handleToggleFiltros}
          >
            <Grid container spacing={2} alignItems="center" pt={1}>
              <Grid item xs={12} sm={6} md={3}>
                <CustomDatePicker
                  label="Vencimiento Desde"
                  value={filtros.fecVenDesde}
                  setValue={(v) => setFiltros((prev) => ({ ...prev, fecVenDesde: v }))}
                  isOptional={true}
                  format="DD/MM/YYYY"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CustomDatePicker
                  label="Vencimiento Hasta"
                  value={filtros.fecVenHasta}
                  setValue={(v) => setFiltros((prev) => ({ ...prev, fecVenHasta: v }))}
                  isOptional={true}
                  format="DD/MM/YYYY"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        color="error"
                        checked={filtros.soloVencidos}
                        onChange={(e) => setFiltros((prev) => ({ ...prev, soloVencidos: e.target.checked }))}
                      />
                    }
                    label={<b>Filtrar solo Lotes Vencidos (Merma)</b>}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        color="warning"
                        checked={filtros.soloPorVencer}
                        onChange={(e) => setFiltros((prev) => ({ ...prev, soloPorVencer: e.target.checked }))}
                      />
                    }
                    label={<b>Filtrar solo Riesgo (Por Vencer)</b>}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<SearchIcon />}
                  onClick={handleEjecutarBusqueda}
                  sx={{ px: 4, fontWeight: "bold", borderRadius: "8px" }}
                >
                  Ejecutar Búsqueda
                </Button>
              </Grid>
            </Grid>
          </CustomFieldsetAccordion>

          {/* =================================================================== */}
          {/* SECCIÓN C: TABLA DINÁMICA DE INTELIGENCIA                           */}
          {/* =================================================================== */}
          <Box mt={3} flexGrow={1}>
            {hasSearched && tablaData.length > 0 && (
              <>
                <Stack direction="row" justifyContent="flex-end" mb={1}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportarCSV}
                    sx={{ fontWeight: "bold", bgcolor: "white" }}
                  >
                    Exportar a Excel / CSV
                  </Button>
                </Stack>

                <CustomAggregationSegregationComponent
                  data={tablaData}
                  columnsConfig={({ totalSumStockBruto, totalSumStockUtil, totalSumVencidos, totalSumPorVencer }) => [
                    { accessorKey: "boddescri", header: "Bodega", size: 150 },
                    { accessorKey: "familia", header: "Familia", size: 150 },
                    { accessorKey: "marca", header: "Marca", size: 150 },
                    {
                      accessorKey: "artcodigo",
                      header: "Código",
                      size: 120,
                      Cell: ({ cell }) => <b>{cell.getValue()}</b>,
                    },
                    { accessorKey: "artdescri", header: "Descripción del Producto", size: 300 },
                    { accessorKey: "zona", header: "Zona", size: 120 },
                    { accessorKey: "pasillo", header: "Pasillo", size: 100 },
                    { accessorKey: "posicion", header: "Posición", size: 100 },
                    {
                      accessorKey: "stock_bruto",
                      header: "Stock Físico",
                      size: 150,
                      accessorFn: (row) => Number(row.stock_bruto) || 0,
                      aggregationFn: "sum",
                      AggregatedCell: ({ cell }) => (
                        <Box color="primary.main" fontWeight="bold">
                          {cell.getValue().toLocaleString()}
                        </Box>
                      ),
                      Cell: ({ cell }) => (
                        <span style={{ fontWeight: "bold", color: "#196C87" }}>{cell.getValue().toLocaleString()}</span>
                      ),
                      Footer: () => (
                        <Stack>
                          Total Físico:
                          <Box color="primary.main" fontWeight="bold">
                            {Number(totalSumStockBruto).toLocaleString()}
                          </Box>
                        </Stack>
                      ),
                    },
                    {
                      accessorKey: "stock_util",
                      header: "Stock Útil",
                      size: 150,
                      accessorFn: (row) => Number(row.stock_util) || 0,
                      aggregationFn: "sum",
                      AggregatedCell: ({ cell }) => (
                        <Box color="success.main" fontWeight="bold">
                          {cell.getValue().toLocaleString()}
                        </Box>
                      ),
                      Cell: ({ cell }) => (
                        <span style={{ fontWeight: "bold", color: "#2e7d32" }}>{cell.getValue().toLocaleString()}</span>
                      ),
                      Footer: () => (
                        <Stack>
                          Total Útil:
                          <Box color="success.main" fontWeight="bold">
                            {Number(totalSumStockUtil).toLocaleString()}
                          </Box>
                        </Stack>
                      ),
                    },
                    {
                      accessorKey: "vencidos",
                      header: "Lotes Vencidos",
                      size: 150,
                      accessorFn: (row) => Number(row.vencidos) || 0,
                      aggregationFn: "sum",
                      AggregatedCell: ({ cell }) => (
                        <Box color="error.main" fontWeight="bold">
                          {cell.getValue().toLocaleString()}
                        </Box>
                      ),
                      Cell: ({ cell }) => {
                        const val = cell.getValue()
                        return val > 0 ? (
                          <Chip label={val.toLocaleString()} color="error" size="small" sx={{ fontWeight: "bold" }} />
                        ) : (
                          <span>0</span>
                        )
                      },
                      Footer: () => (
                        <Stack>
                          Total Vencidos:
                          <Box color="error.main" fontWeight="bold">
                            {Number(totalSumVencidos).toLocaleString()}
                          </Box>
                        </Stack>
                      ),
                    },
                    {
                      accessorKey: "por_vencer",
                      header: "Por Vencer (Riesgo)",
                      size: 160,
                      accessorFn: (row) => Number(row.por_vencer) || 0,
                      aggregationFn: "sum",
                      AggregatedCell: ({ cell }) => (
                        <Box color="warning.main" fontWeight="bold">
                          {cell.getValue().toLocaleString()}
                        </Box>
                      ),
                      Cell: ({ cell }) => {
                        const val = cell.getValue()
                        return val > 0 ? (
                          <Chip
                            label={val.toLocaleString()}
                            color="warning"
                            size="small"
                            sx={{ fontWeight: "bold", color: "black" }}
                          />
                        ) : (
                          <span>0</span>
                        )
                      },
                      Footer: () => (
                        <Stack>
                          Total Riesgo:
                          <Box color="warning.main" fontWeight="bold">
                            {Number(totalSumPorVencer).toLocaleString()}
                          </Box>
                        </Stack>
                      ),
                    },
                    {
                      accessorKey: "fecha_vencimiento",
                      header: "Fec. Próx. Caducidad",
                      size: 150,
                      Cell: ({ cell }) => {
                        const val = cell.getValue()
                        return <span>{val ? dayjs(val).format("DD/MM/YYYY") : "N/A"}</span>
                      },
                    },
                  ]}
                  aggregations={{
                    totalSumStockBruto: (data) => data.reduce((acc, curr) => acc + Number(curr.stock_bruto || 0), 0),
                    totalSumStockUtil: (data) => data.reduce((acc, curr) => acc + Number(curr.stock_util || 0), 0),
                    totalSumVencidos: (data) => data.reduce((acc, curr) => acc + Number(curr.vencidos || 0), 0),
                    totalSumPorVencer: (data) => data.reduce((acc, curr) => acc + Number(curr.por_vencer || 0), 0),
                  }}
                  tableOptions={{
                    initialState: { sorting: [{ id: "artdescri", desc: false }] },
                  }}
                />
              </>
            )}

            {/* EMPTY STATE ELEGANTE PARA EJECUTIVOS */}
            {(!hasSearched || (hasSearched && tablaData.length === 0 && !loadTabla && !fetchTabla)) && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "300px",
                  border: "2px dashed #cfd8dc",
                  borderRadius: "16px",
                  backgroundColor: "#ffffff",
                  p: 4,
                  mt: 2,
                }}
              >
                <SearchIcon sx={{ fontSize: 60, color: "#b0bec5", mb: 2 }} />
                <Typography variant="h6" color="textSecondary" fontWeight="bold">
                  {hasSearched
                    ? "No se encontraron resultados con los filtros aplicados."
                    : "El panel de detalle se encuentra inactivo."}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mt: 1, maxWidth: "500px", textAlign: "center" }}
                >
                  {hasSearched
                    ? "Intente ampliar el rango de fechas de vencimiento."
                    : "Utilice la sección 'Auditoría de Vencimientos y Riesgos' arriba y haga clic en 'Ejecutar Búsqueda' para generar el listado detallado de inventario. Esto optimiza el rendimiento del sistema."}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default ResumenProductosBodegas
